import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PublishAccountState } from '../../hooks/usePublishAccount';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Compose left column: a batch dropdown over one project item's render jobs, the
 * chosen batch's videos as a selectable list, and — once more than one is ticked —
 * a bulk Publish / Schedule / Save-as-draft bar backed by POST /api/publish/bulk.
 *
 * Single selection drives the middle + right columns (the embedded PublishDashboard).
 */

const BULK_MAX = 10;

type PublishPlatform = 'youtube' | 'instagram' | 'tiktok' | 'facebook' | 'linkedin' | 'x';

const PLATFORM_LABEL: Record<PublishPlatform, string> = {
  youtube: 'YouTube', instagram: 'Instagram', tiktok: 'TikTok',
  facebook: 'Facebook', linkedin: 'LinkedIn', x: 'X',
};
const PLATFORM_CONTENT_TYPE: Record<PublishPlatform, string> = {
  youtube: 'shorts', instagram: 'reels', tiktok: 'video',
  facebook: 'reels', linkedin: 'post', x: 'post',
};
const PROVIDER_TO_PLATFORM: Record<string, PublishPlatform> = {
  google: 'youtube', youtube: 'youtube', instagram: 'instagram',
  facebook: 'facebook', tiktok: 'tiktok', twitter: 'x', x: 'x', linkedin: 'linkedin',
};

interface RailOutput {
  shortId: string | null;
  index: number;
  title: string | null;
  url: string | null;
  durationSeconds: number | null;
  score: number | null;
  publishStatus: string | null;
}

interface RailJob {
  id: string;
  status: string;
  createdAt: string;
  /** Present on caption / story job rows — the video the render's clip belongs to. */
  videoId?: string | null;
  /** The raw upload itself, stood in for a batch when nothing has been rendered yet. */
  isSource?: boolean;
  outputs: RailOutput[];
}

interface BulkResult {
  videoId: string;
  shortId: string;
  outcome: string;
  reason?: string;
}

export type ComposeItemKind = 'shorts' | 'caption' | 'story';

interface Props {
  kind: ComposeItemKind;
  /** videoId for a shorts item; caption-workspace / image-project id otherwise. */
  sourceId: string;
  /** Needed to read a raw upload (no render jobs) back through the project's video route. */
  projectId?: string;
  selectedShortId?: string;
  onSelectShort: (shortId: string) => void;
  /** Reports the real videoId behind the current selection (needed for caption/story). */
  onResolveVideoId?: (videoId: string) => void;
  /** Bump this to force a refetch (e.g. after a single publish in the middle column). */
  refreshKey?: number;
  /** YouTube account chosen in the compose header; the whole batch is published to it. */
  publishAccount?: PublishAccountState;
}

function jobsUrlFor(kind: ComposeItemKind, id: string): string {
  const encoded = encodeURIComponent(id);
  if (kind === 'caption') return `${API_BASE}/api/caption-workspaces/${encoded}/jobs`;
  if (kind === 'story') return `${API_BASE}/api/image-projects/${encoded}/jobs`;
  return `${API_BASE}/api/videos/${encoded}/jobs`;
}

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  published: { label: '🌐 Published', cls: 'cbr-badge--published' },
  scheduled: { label: '📅 Scheduled', cls: 'cbr-badge--scheduled' },
  draft: { label: '📝 Draft', cls: 'cbr-badge--draft' },
  publishing: { label: '🔄 Publishing', cls: 'cbr-badge--publishing' },
  queued: { label: '🔄 Publishing', cls: 'cbr-badge--publishing' },
  failed: { label: '❌ Failed', cls: 'cbr-badge--failed' },
};

function fmtDuration(sec: number | null): string {
  if (!sec || sec <= 0) return '';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const ComposeBatchRail: React.FC<Props> = ({
  kind, sourceId, projectId, selectedShortId, onSelectShort, onResolveVideoId, refreshKey = 0, publishAccount,
}) => {
  const [jobs, setJobs] = useState<RailJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [batchIdx, setBatchIdx] = useState(0);

  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [connected, setConnected] = useState<PublishPlatform[]>([]);
  const [bulkPlatforms, setBulkPlatforms] = useState<Set<PublishPlatform>>(new Set());
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleAt, setScheduleAt] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<BulkResult[] | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  // Finished batches only (oldest first, so "Batch 1" is the first render).
  const finishedJobs = useMemo(
    () => [...jobs]
      .filter(j => (j.outputs || []).some(o => o.shortId && o.url))
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
    [jobs]
  );

  const activeJob = finishedJobs[batchIdx] || null;
  const activeOutputs = useMemo(
    () => (activeJob?.outputs || []).filter(o => o.shortId && o.url),
    [activeJob]
  );

  // shortId → the video it belongs to. For a shorts item that is always `sourceId`;
  // for caption / story the render job carries its own videoId.
  const videoIdFor = useCallback((job: RailJob | null): string => {
    if (kind === 'shorts') return sourceId;
    return job?.videoId || '';
  }, [kind, sourceId]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetch(jobsUrlFor(kind, sourceId), { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error('Failed to load renders'))))
      .then(async (data: RailJob[]) => {
        const list = Array.isArray(data) ? data : [];
        const hasRender = list.some(j => (j.outputs || []).some(o => o.shortId && o.url));
        // A video posted as-is has no render job, only its whole-file short — list that instead.
        if (!hasRender && kind === 'shorts' && projectId) {
          const source = await fetch(
            `${API_BASE}/api/projects/${encodeURIComponent(projectId)}/videos/${encodeURIComponent(sourceId)}`,
            { credentials: 'include' }
          ).then(r => (r.ok ? r.json() : null)).catch(() => null);
          const shorts: Array<{ id: string; index: number; title: string; fileUrl: string; duration: number }> =
            Array.isArray(source?.shorts) ? source.shorts : [];
          if (shorts.length > 0) {
            list.push({
              id: `source-${sourceId}`,
              status: 'completed',
              createdAt: source.created_at || new Date().toISOString(),
              isSource: true,
              outputs: shorts.map(sh => ({
                shortId: sh.id, index: sh.index, title: sh.title, url: sh.fileUrl,
                durationSeconds: sh.duration || null, score: null, publishStatus: null,
              })),
            });
          }
        }
        setJobs(list);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load renders'))
      .finally(() => setLoading(false));
  }, [kind, sourceId, projectId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    fetch(`${API_BASE}/api/accounts`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : { connectedPlatforms: [] }))
      .then(data => {
        const list = (data.connectedPlatforms || [])
          .map((v: string) => PROVIDER_TO_PLATFORM[v] || v)
          .filter((v: string): v is PublishPlatform => v in PLATFORM_LABEL);
        const unique = Array.from(new Set<PublishPlatform>(list));
        setConnected(unique);
        setBulkPlatforms(new Set(unique));
      })
      .catch(() => { /* leave empty */ });
  }, []);

  // On first load, jump to the batch that holds a deep-linked shortId.
  const jumpedRef = useRef(false);
  useEffect(() => {
    if (jumpedRef.current || finishedJobs.length === 0 || !selectedShortId) return;
    const idx = finishedJobs.findIndex(j => j.outputs.some(o => o.shortId === selectedShortId));
    jumpedRef.current = true;
    if (idx > 0) setBatchIdx(idx);
  }, [finishedJobs, selectedShortId]);

  // Keep the batch index in range and drive the single selection to the first video.
  useEffect(() => {
    if (finishedJobs.length === 0) return;
    if (batchIdx > finishedJobs.length - 1) { setBatchIdx(0); return; }
    const inThisBatch = activeOutputs.some(o => o.shortId === selectedShortId);
    if (!inThisBatch && activeOutputs[0]?.shortId) {
      onSelectShort(activeOutputs[0].shortId);
    }
  }, [finishedJobs, batchIdx, activeOutputs, selectedShortId, onSelectShort]);

  // Report the real videoId behind the current batch (caption/story need it for the composer).
  useEffect(() => {
    const vid = videoIdFor(activeJob);
    if (vid) onResolveVideoId?.(vid);
  }, [activeJob, videoIdFor, onResolveVideoId]);

  const toggleCheck = (shortId: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(shortId)) next.delete(shortId);
      else if (next.size < BULK_MAX) next.add(shortId);
      return next;
    });
  };

  const toggleBulkPlatform = (p: PublishPlatform) => {
    setBulkPlatforms(prev => {
      const next = new Set(prev);
      next.has(p) ? next.delete(p) : next.add(p);
      return next;
    });
  };

  const runBulk = async (mode: 'immediate' | 'scheduled' | 'draft') => {
    if (submitting || checked.size === 0) return;
    if (mode !== 'draft' && bulkPlatforms.size === 0) {
      setBulkError('Pick at least one platform.');
      return;
    }
    if (mode === 'scheduled' && !scheduleAt) {
      setBulkError('Pick a date and time to schedule.');
      return;
    }
    if (mode !== 'draft' && bulkPlatforms.has('youtube') && publishAccount?.needsChoice) {
      setBulkError('Choose the YouTube account first (account chip at the top of the page).');
      return;
    }

    setSubmitting(true);
    setBulkError(null);
    setResults(null);
    try {
      const res = await fetch(`${API_BASE}/api/publish/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          items: Array.from(checked).map(shortId => {
            const job = finishedJobs.find(j => j.outputs.some(o => o.shortId === shortId)) || null;
            return { videoId: videoIdFor(job), shortId };
          }).filter(it => it.videoId),
          platforms: Array.from(bulkPlatforms).map(p => ({
            platform: p,
            contentType: PLATFORM_CONTENT_TYPE[p],
            ...(p === 'youtube' && publishAccount?.selectedId ? { linkedAccountId: publishAccount.selectedId } : {}),
          })),
          scheduleType: mode,
          scheduledTime: mode === 'scheduled' ? new Date(scheduleAt).toISOString() : undefined,
          timezone: 'PST',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Bulk action failed');
      }
      setResults(Array.isArray(data.results) ? data.results : []);
      setChecked(new Set());
      load();
    } catch (err: unknown) {
      setBulkError(err instanceof Error ? err.message : 'Bulk action failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="cbr"><div className="cbr-state">Loading renders…</div></div>;
  if (error) {
    return (
      <div className="cbr">
        <div className="cbr-state">
          {error}
          <button className="btn-sm" style={{ marginTop: 10 }} onClick={load}>Retry</button>
        </div>
      </div>
    );
  }
  if (finishedJobs.length === 0) {
    return (
      <div className="cbr">
        <div className="cbr-state">
          No renders yet for this video. Open it in the clip editor and render a batch first.
        </div>
      </div>
    );
  }

  const multi = checked.size >= 1;

  return (
    <div className="cbr">
      <label className="cbr-label" htmlFor="cbr-batch">Batch</label>
      <select
        id="cbr-batch"
        className="cbr-select"
        value={batchIdx}
        onChange={e => { setBatchIdx(Number(e.target.value)); setResults(null); }}
      >
        {finishedJobs.map((j, i) => (
          <option key={j.id} value={i}>
            {j.isSource
              ? 'Uploaded video'
              : `Batch ${i + 1} · ${fmtDate(j.createdAt)} · ${j.outputs.filter(o => o.shortId && o.url).length} video(s)`}
          </option>
        ))}
      </select>

      {activeOutputs.length === 0 ? (
        <div className="cbr-state">This batch has no finished videos yet.</div>
      ) : (
        <div className="cbr-list">
          {activeOutputs.map(o => {
            const badge = o.publishStatus ? STATUS_BADGE[o.publishStatus] : null;
            const isSelected = o.shortId === selectedShortId;
            return (
              <div
                key={o.shortId}
                className={`cbr-row ${isSelected ? 'cbr-row--active' : ''}`}
                onClick={() => o.shortId && onSelectShort(o.shortId)}
              >
                <input
                  type="checkbox"
                  className="cbr-check"
                  checked={o.shortId ? checked.has(o.shortId) : false}
                  disabled={!!o.shortId && !checked.has(o.shortId) && checked.size >= BULK_MAX}
                  onClick={e => e.stopPropagation()}
                  onChange={() => o.shortId && toggleCheck(o.shortId)}
                />
                <div className="cbr-row-main">
                  <span className="cbr-row-title">{o.title || `Video #${o.index + 1}`}</span>
                  <span className="cbr-row-meta">
                    {[fmtDuration(o.durationSeconds), o.score ? `${o.score}/10` : '']
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
                {badge && <span className={`cbr-badge ${badge.cls}`}>{badge.label}</span>}
              </div>
            );
          })}
        </div>
      )}

      {multi && (
        <div className="cbr-bulk">
          <div className="cbr-bulk-head">{checked.size} selected{checked.size >= BULK_MAX ? ` (max ${BULK_MAX})` : ''}</div>

          <div className="cbr-bulk-platforms">
            {connected.length === 0 ? (
              <span className="cbr-row-meta">No connected platforms.</span>
            ) : connected.map(p => (
              <button
                key={p}
                className={`cbr-chip ${bulkPlatforms.has(p) ? 'cbr-chip--on' : ''}`}
                onClick={() => toggleBulkPlatform(p)}
              >
                {PLATFORM_LABEL[p]}
              </button>
            ))}
          </div>

          {bulkPlatforms.has('youtube') && publishAccount?.selected && (
            <div className="cbr-row-meta">YouTube account: {publishAccount.selected.name}</div>
          )}

          <label className="cbr-bulk-schedrow">
            <input
              type="checkbox"
              checked={scheduleEnabled}
              onChange={e => setScheduleEnabled(e.target.checked)}
            />
            Schedule
          </label>
          {scheduleEnabled && (
            <input
              type="datetime-local"
              className="cbr-select"
              value={scheduleAt}
              onChange={e => setScheduleAt(e.target.value)}
            />
          )}

          {bulkError && <div className="cbr-bulk-error">⚠️ {bulkError}</div>}

          <div className="cbr-bulk-actions">
            {scheduleEnabled ? (
              <button className="btn-primary btn-sm" disabled={submitting} onClick={() => runBulk('scheduled')}>
                {submitting ? '…' : `Schedule ${checked.size}`}
              </button>
            ) : (
              <button className="btn-primary btn-sm" disabled={submitting} onClick={() => runBulk('immediate')}>
                {submitting ? '…' : `Publish ${checked.size}`}
              </button>
            )}
            <button className="btn-secondary btn-sm" disabled={submitting} onClick={() => runBulk('draft')}>
              Save as draft
            </button>
          </div>
          <div className="cbr-row-meta">
            Each video keeps its own title, caption and tags. Videos missing a caption are saved as drafts.
          </div>
        </div>
      )}

      {results && (
        <div className="cbr-results">
          <div className="cbr-bulk-head">Result</div>
          {results.map(r => (
            <div key={r.shortId} className="cbr-result-row">
              <span className={`cbr-badge ${STATUS_BADGE[r.outcome]?.cls || 'cbr-badge--draft'}`}>
                {STATUS_BADGE[r.outcome]?.label || r.outcome}
              </span>
              {r.reason && <span className="cbr-row-meta">{r.reason}</span>}
            </div>
          ))}
          {results.some(r => r.outcome === 'draft') && (
            <a href="/posts/drafts" className="cbr-row-meta" style={{ textDecoration: 'underline' }}>
              View drafts
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default ComposeBatchRail;
